import { Component, type ErrorInfo, type ReactNode } from "react";

import { Button, EmptyState } from "./ui";

const RELOAD_KEY = "rp-chunk-reload";

/**
 * A chunk that no longer exists: the tab was opened before a deploy, and the
 * screen it now asks for was renamed by the new build. Reloading fetches the
 * new index and the new names.
 */
function isStaleChunk(error: unknown): boolean {
  const message = error instanceof Error ? error.message : String(error);
  return /dynamically imported module|Importing a module script failed|Failed to fetch dynamically imported|error loading dynamically imported/i.test(message);
}

/**
 * Without this, any render error unmounts the whole tree and the venue sees a
 * black page with no way back - which is what a null in one stats list did.
 * Inside the panel shell it sits under the navigation (keyed by path), so a
 * broken screen leaves the tabs working; the app-level one is the last resort
 * for the scanners and the claim page.
 */
export class ErrorBoundary extends Component<{ children: ReactNode }, { error: unknown }> {
  state: { error: unknown } = { error: null };

  static getDerivedStateFromError(error: unknown) {
    return { error };
  }

  componentDidCatch(error: unknown, info: ErrorInfo) {
    console.error("screen failed to render", error, info.componentStack);
    if (!isStaleChunk(error)) return;
    // One automatic reload per stale chunk; a second failure within a few
    // seconds means something else is wrong, and the message stays up.
    let last = 0;
    try {
      last = Number(sessionStorage.getItem(RELOAD_KEY) ?? 0);
      if (Date.now() - last > 10_000) {
        sessionStorage.setItem(RELOAD_KEY, String(Date.now()));
        window.location.reload();
      }
    } catch {
      // Storage blocked: fall back to the button.
    }
  }

  render() {
    if (!this.state.error) return this.props.children;
    const stale = isStaleChunk(this.state.error);
    return (
      <div className="px-4 py-10">
        <EmptyState
          variant="error"
          title={stale ? "Panel został zaktualizowany" : "Ten ekran się nie wczytał"}
          hint={stale ? "Odśwież stronę, żeby wczytać nową wersję." : "Odśwież stronę. Jeśli błąd wraca, daj znać obsłudze RewardPass."}
          action={
            <Button size="sm" onClick={() => window.location.reload()}>
              Odśwież
            </Button>
          }
          className="mx-auto max-w-[480px]"
        />
      </div>
    );
  }
}
